import { ApolloProvider, getApolloContext } from "@apollo/client";
import type { NextPage } from "next";
import { useContext } from "react";
import type { ReactElement, ReactNode } from "react";
import { useApollo } from "./apolloClient";

type PageWithLayout<P> = NextPage<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

// Provides Apollo to a single page. Apollo is kept out of _app (see
// webpack/ForbidAppModulesPlugin.js) so pages that don't use it don't download it.
//
// Files under pages/ that are also imported as components by other pages are
// wrapped too, because Next.js serves them as routes; when rendered inside an
// already wrapped page, the existing client is reused.
export function withApollo<P extends object>(Page: PageWithLayout<P>) {
  function ApolloPage(props: P) {
    const client = useApollo(props);
    return (
      <ApolloProvider client={client}>
        <Page {...props} />
      </ApolloProvider>
    );
  }

  const WithApollo: PageWithLayout<P> = (props: P) => {
    const { client } = useContext(getApolloContext());
    return client ? <Page {...props} /> : <ApolloPage {...props} />;
  };

  WithApollo.getLayout = Page.getLayout;
  WithApollo.displayName = `withApollo(${Page.displayName || Page.name || "Page"})`;

  return WithApollo;
}
